import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DEFAULT_LABEL_PREFIX, gridCellLabels } from '@/lib/slotLabels'
import { LabelSchemePicker } from './LabelSchemePicker'
import { useDeclareGrid } from '@/hooks/useStorage'
import {
  STORAGE_NODE_KINDS,
  STORAGE_NODE_KIND_LABELS,
  type ApiStorageNode,
  type SlotLabelScheme,
  type StorageNodeKind,
} from '@/types/storage'

/**
 * The two grid-declaration controls, shared by the Equipment-side dialog
 * (`StorageLayoutDialog`) and the Inventory → Storage Setup tab. They are one
 * implementation on purpose: a grid declared from either surface must mint the
 * same codes, and two copies would drift the moment one gained a field.
 */

/**
 * Node codes exclude the path separator (it would forge a level) and must
 * start alphanumeric — mirror of the backend code pattern.
 */
export function sanitizeCode(label: string): string {
  return label
    .replace(/\//g, '-')
    .replace(/[^A-Za-z0-9 _.-]/g, '')
    .replace(/^[^A-Za-z0-9]+/, '')
    .trim()
    .slice(0, 64)
}

/** Grid child kinds a declaration may mint. Only structural/leaf kinds below a unit. */
export const CHILD_KINDS = STORAGE_NODE_KINDS.filter(
  (k): k is 'ZONE' | 'RACK' | 'BOX' | 'POSITION' =>
    k === 'ZONE' || k === 'RACK' || k === 'BOX' || k === 'POSITION',
)

export const defaultScheme = (childKind: StorageNodeKind): SlotLabelScheme =>
  childKind === 'POSITION' ? 'ROW_COLUMN' : 'SEQUENTIAL'

export function DeclareGridForm({ node }: { node: ApiStorageNode }) {
  const declareGrid = useDeclareGrid()
  const [rows, setRows] = useState('1')
  const [columns, setColumns] = useState('1')
  const [childKind, setChildKind] = useState<StorageNodeKind>('POSITION')
  const [scheme, setScheme] = useState<SlotLabelScheme>('ROW_COLUMN')
  const [prefix, setPrefix] = useState('')

  const effPrefix = prefix || DEFAULT_LABEL_PREFIX[childKind] || 'N'

  // The preview runs the SAME minting function the backend uses, so what it
  // shows is what will be created rather than a hand-written approximation.
  let preview: string | null = null
  let previewError: string | null = null
  try {
    const cells = gridCellLabels({
      rows: Number(rows),
      columns: Number(columns),
      scheme,
      prefix: effPrefix,
    })
    const first = cells[0].code
    const last = cells[cells.length - 1].code
    const kind = STORAGE_NODE_KIND_LABELS[childKind].toLowerCase()
    preview =
      cells.length === 1
        ? `One ${kind}, named ${first}`
        : `${cells.length} ${kind}s, named ${first} through ${last}`
  } catch (e) {
    previewError = e instanceof Error ? e.message : String(e)
  }

  return (
    <section className='rounded-lg border bg-card'>
      <header className='border-b px-4 py-3'>
        <h3 className='text-sm font-semibold'>Describe what is inside {node.code}</h3>
        <p className='mt-0.5 text-xs text-muted-foreground'>
          Lay out a grid and every place in it is created and named for you.
        </p>
      </header>

      <div className='space-y-4 px-4 py-4'>
        {/* Sized to the values, not to the card: two small numbers and a short
            list of kinds. Stretching the kind picker across a wide panel made
            it read as the most important field on the form. */}
        <div className='grid grid-cols-2 gap-3 sm:grid-cols-[5.5rem_5.5rem_13rem] sm:justify-start'>
          <div className='space-y-1.5'>
            <Label htmlFor='grid-rows'>Rows</Label>
            <Input
              id='grid-rows'
              type='number'
              min={1}
              value={rows}
              onChange={(e) => setRows(e.target.value)}
            />
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='grid-columns'>Columns</Label>
            <Input
              id='grid-columns'
              type='number'
              min={1}
              value={columns}
              onChange={(e) => setColumns(e.target.value)}
            />
          </div>
          <div className='space-y-1.5'>
            <Label>Each holding a</Label>
            <Select
              value={childKind}
              onValueChange={(v) => {
                const kind = v as StorageNodeKind
                setChildKind(kind)
                setScheme(defaultScheme(kind))
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CHILD_KINDS.map((kind) => (
                  <SelectItem key={kind} value={kind}>
                    {STORAGE_NODE_KIND_LABELS[kind]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <LabelSchemePicker
          rows={Number(rows)}
          columns={Number(columns)}
          childKind={childKind}
          scheme={scheme}
          prefix={prefix}
          onChange={(next) => {
            setScheme(next.scheme)
            setPrefix(next.prefix ?? '')
          }}
        />
        {/* What you are about to create, in the same breath as the button that
          creates it — and minted by the function the server uses, so it is the
          result rather than an illustration of it. */}
        <div className='flex flex-wrap items-center justify-between gap-3 border-t pt-4'>
          <p className='text-sm text-muted-foreground'>
            {previewError ? <span className='text-destructive'>{previewError}</span> : preview}
          </p>
          <Button
            disabled={declareGrid.isPending || !!previewError}
            onClick={() =>
              declareGrid.mutate({
                id: node.id,
                rows: Number(rows),
                columns: Number(columns),
                childKind,
                scheme,
                ...(scheme === 'SEQUENTIAL' ? { prefix: effPrefix } : {}),
              })
            }
          >
            {declareGrid.isPending ? 'Creating…' : 'Create them'}
          </Button>
        </div>
        {declareGrid.error && (
          <p className='text-sm text-destructive'>{String(declareGrid.error.message)}</p>
        )}
      </div>
    </section>
  )
}
